import { motion } from "framer-motion";
import {
  Clock10,
  DollarSign,
  Smartphone,
  Languages,
  MessageCircle,
  Cloud,
} from "lucide-react";

export const WhyChooseMe = () => {
  const items = [
    {
      icon: <Clock10 size={28} className="text-blue-600" />,
      label: "Fast Delivery (5–8 Days)",
      description: "Get your website ready in just a few days without compromising on quality"
    },
    {
      icon: <DollarSign size={28} className="text-green-600" />,
      label: "Affordable Pricing",
      description: "Professional websites at competitive prices with transparent pricing"
    },
    {
      icon: <Smartphone size={28} className="text-purple-600" />,
      label: "100% Mobile Friendly",
      description: "Responsive designs that work perfectly on all devices and screen sizes"
    },
    {
      icon: <Languages size={28} className="text-orange-500" />,
      label: "Urdu + English Websites",
      description: "Bilingual website support to reach a wider audience effectively"
    },
    {
      icon: <MessageCircle size={28} className="text-pink-600" />,
      label: "WhatsApp Support",
      description: "Direct communication channel for quick updates and support"
    },
    {
      icon: <Cloud size={28} className="text-cyan-600" />,
      label: "Hosting Setup Guide",
      description: "Complete assistance with deployment and hosting configuration"
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100 px-6 md:px-12 relative overflow-hidden" id="why-choose-me">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-40 h-40 bg-blue-500 rounded-full opacity-5 -translate-x-20 -translate-y-20"></div>
      <div className="absolute bottom-0 right-0 w-60 h-60 bg-emerald-500 rounded-full opacity-5 translate-x-20 translate-y-20"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <motion.h2
            className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-emerald-500 text-transparent bg-clip-text inline-block mb-4"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Work With Me
          </motion.h2>
          <motion.p
            className="text-lg text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Discover the advantages of choosing a dedicated web developer who prioritizes your success
          </motion.p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 items-center">
          {/* Left Content - Features Grid */}
          <motion.div
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map((item, i) => (
                <motion.div
                  key={i}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group hover:-translate-y-1"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start gap-4 mb-3">
                    <div className="p-3 bg-gray-50 rounded-xl shadow-sm group-hover:scale-110 transition-transform duration-300">
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                      {item.label}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm pl-16 group-hover:text-gray-800 transition-colors duration-300">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Image and Stats */}
          <motion.div
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                alt="Why choose me"
                className="w-full h-64 object-cover"
              />

              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">My Commitment to You</h3>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <div className="text-2xl font-bold text-blue-600">20+</div>
                    <div className="text-sm text-gray-600">Projects Completed</div>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-xl">
                    <div className="text-2xl font-bold text-emerald-600">100%</div>
                    <div className="text-sm text-gray-600">Client Satisfaction</div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-gray-700 italic text-sm">
                    "I believe in building long-term relationships with my clients by delivering exceptional
                    results and providing ongoing support even after project completion."
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              onClick={() => { window.location.href = 'https://wa.me/923112891862?text=Hi%20Momin%2C%20I%20want%20to%20discuss%20a%20project'; }}

            >
              <button className="bg-gradient-to-r from-blue-600 to-emerald-500 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:from-blue-700 hover:to-emerald-600">
                Start Your Project Today
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};